import type { PropsWithChildren, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import type { RezaOpravneniDto, RezaPortalInfo } from '@gov-nx/api';
import {
	usePoQuery,
	rezaEligibleDetail,
	rezaDownloadQuery,
	useDownloadPoMutation,
	useAxiosInstance,
} from '@gov-nx/api';
import type { ExtraConfig } from '@gov-nx/core/app';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import type { Optional } from '@gov-nx/core/types';
import { is } from '@gov-nx/core/types';
import { type PageCode } from '@gov-nx/module/page';
import type { IUseBoolean } from '@gov-nx/utils/common';
import { toDateReadable, useBoolean } from '@gov-nx/utils/common';
import type { ComplaintsForm } from '../useComplaintsForm';
import { useComplaintsForm } from '../useComplaintsForm';
import { usePortalInfo } from '../usePortalInfo';
import { useRezaLocale } from '../useRezaLocale';
import { zastupujiciOsobaNazev } from '../utils';
import {
	type UseRezaSendByDataBoxForm,
	useRezaSendByDataBoxForm,
} from './formDefinition';

export interface RezaDetailContexts {
	code: PageCode;
	isLoading: boolean;
	isError: boolean;
	data?: RezaOpravneniDto;
	portalInfo?: RezaPortalInfo;
	name: string;
	description: Optional<string>;
	postoupeni: {
		title: string;
		keyValues: { key: string; value?: string | number }[];
	}[];
	onDownload: () => void;
	isDownloading: boolean;
	complaintsForm: ComplaintsForm;
	sendByDataBoxForm: UseRezaSendByDataBoxForm;
	isEditModalOpen: IUseBoolean;
	keyValues: {
		key: string;
		tooltip?: { children: ReactNode };
		value?: string | number;
		canCopy?: boolean;
	}[];
}

const Context = createContext<RezaDetailContexts | null>(null);

type Props = PropsWithChildren<{
	code: PageCode;
	id: string;
	onInvalidated: () => void;
}> &
	ExtraConfig['pageRoutes']['rezaDetailConfig']['rezaDetailContextProvider'];

export const RezaDetailContextProvider = ({
	children,
	code,
	id,
	onInvalidated,
	...extraConfig
}: Props) => {
	const { t } = useRezaLocale();
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { axiosInstance } = useAxiosInstance();

	const query = usePoQuery(
		{
			queryKey: ['reza', 'detail', id],
			queryFn: async () => rezaEligibleDetail(axiosInstance, id),
		},
		{ errorIgnoreFilter: () => true }
	);

	const portalInfoQuery = usePortalInfo(query.data?.kodSablony);

	const { download } = useDownloadEvents();

	const mutation = useDownloadPoMutation(rezaDownloadQuery, {
		onError: () => toastMessageError(t('toasts.download.chyba')),
		onSuccess: (data) =>
			download({
				blob: data.blob,
				id: id,
				fileName: data.fileName,
				antivirus: data.antivirus,
				callback: (id, success, error) => {
					if (success) {
						toastMessageSuccess(
							t('toasts.download.success', { fileName: data.fileName })
						);
					}
					if (error) {
						toastMessageError(t('toasts.download.chyba'));
					}
				},
			}),
	});

	const onDownload = async () =>
		mutation.mutateAsync({
			axiosInstance,
			code: id,
		});

	const postoupeni: RezaDetailContexts['postoupeni'] =
		query.data?.seznamPostoupeni?.filter(is).map((data) => ({
			title: zastupujiciOsobaNazev(data) ?? '',
			keyValues: [
				...(data?.zastupujiciOsobaTyp === 'FO'
					? [
							{
								key: t('detail.table.fo-datum-narozeni'),
								value: data?.zastupujiciOsoba?.fyzickaOsoba?.datumNarozeni
									? toDateReadable(
											data?.zastupujiciOsoba?.fyzickaOsoba?.datumNarozeni
										)
									: undefined,
							},
							{
								key: t('detail.table.fo-adresa'),
								value: data?.zastupujiciOsoba?.fyzickaOsoba?.adresa?.adresaText,
							},
						]
					: []),
				...(data?.zastupujiciOsobaTyp === 'PO'
					? [
							{
								key: t('detail.table.po-ico'),
								value: data.zastupujiciOsoba?.pravnickaOsoba?.ico,
							},
							{
								key: t('detail.table.po-sidlo'),
								value:
									data.zastupujiciOsoba?.pravnickaOsoba?.adresa?.adresaText,
							},
						]
					: []),
				{ key: t('detail.table.identifikator'), value: data.kod },
				{
					key: t('detail.table.platnost-od'),
					value: data?.platnostOd ? toDateReadable(data.platnostOd) : undefined,
				},
			],
		})) ?? [];

	return (
		<Context.Provider
			value={{
				code,
				isLoading: query.isLoading || portalInfoQuery.isLoading,
				isError: query.isError || portalInfoQuery.isError,
				data: query.data,
				portalInfo: portalInfoQuery.data,
				name: extraConfig.isSubjectCmsName
					? zastupujiciOsobaNazev(query.data ?? {}, { showIco: false })
					: (query.data?.nazevSablony ?? ''),
				description: extraConfig.overrideCmsDescription
					? t('detail.description')
					: undefined,
				onDownload,
				isDownloading: mutation.isLoading,
				complaintsForm: useComplaintsForm(id),
				sendByDataBoxForm: useRezaSendByDataBoxForm(id),
				isEditModalOpen: useBoolean(),
				keyValues: extraConfig.keyValues(query.data ?? {}, t),
				postoupeni,
			}}>
			{children}
		</Context.Provider>
	);
};

export const useRezaDetailContext = () => {
	const context = useContext(Context);
	if (!context) {
		throw new Error(
			'useRezaDetailContext must be used within a DrozdRegistrationDetailContextProvider'
		);
	}
	return context;
};
